import React, { useMemo, useRef, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { coreAPI, MAX_VIDEO_SIZE, uploadVideoFile } from '../services/api';
import { useToast } from '../context/ToastContext';
import './RichTextEditor.css';

const BlockEmbed = Quill.import('blots/block/embed');

class Html5Video extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('src', value);
    node.setAttribute('controls', 'controls');
    node.setAttribute('playsinline', '');
    node.setAttribute('preload', 'metadata');
    node.setAttribute('style', 'width:100%;max-height:480px;background:#000;');
    return node;
  }

  static value(node) {
    return node.getAttribute('src');
  }
}

Html5Video.blotName = 'html5Video';
Html5Video.tagName = 'VIDEO';
Quill.register(Html5Video, true);

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link', 'image', 'video'],
  ['clean'],
];

const RichTextEditor = ({ label, value, onChange, placeholder, minHeight = 180 }) => {
  const quillRef = useRef(null);
  const { error: showError } = useToast();

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await coreAPI.adminUploadImage(formData);
        const url = response.data?.url;
        if (!url) {
          showError('圖片上傳失敗');
          return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', url);
        editor.setSelection(range.index + 1);
      } catch (err) {
        console.error('Image upload failed:', err);
        showError(err.response?.data?.error || '圖片上傳失敗');
      }
    };
  }, [showError]);

  const videoHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'video/mp4,video/webm,video/ogg,.mp4,.webm,.ogg');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (file.size > MAX_VIDEO_SIZE) {
        showError('影片大小不能超過 300MB');
        return;
      }

      try {
        const response = await uploadVideoFile(file, 'content');
        const url = response?.url;
        if (!url) {
          showError('影片上傳失敗');
          return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'html5Video', url, 'user');
        editor.setSelection(range.index + 1);
      } catch (err) {
        console.error('Video upload failed:', err);
        showError(err.response?.data?.error || err.message || '影片上傳失敗');
      }
    };
  }, [showError]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: TOOLBAR_OPTIONS,
        handlers: {
          image: imageHandler,
          video: videoHandler,
        },
      },
    }),
    [imageHandler, videoHandler]
  );

  return (
    <div className="rich-text-editor">
      {label && <label className="rich-text-editor-label">{label}</label>}
      <div className="rich-text-editor-wrapper" style={{ '--editor-min-height': `${minHeight}px` }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
